/**
 * Messaging Pipeline Contracts
 *
 * Every cross-file invariant in the messaging pipeline lives here.
 * Import from this module instead of duplicating magic values across files.
 */

// Cache key contract
// RTK Query caches by full parameter signature. All cache updates MUST use
// these exact params or the update silently misses.
export const MESSAGES_CACHE_PARAMS = { page: 0, limit: 100 } as const;

export function messagesCacheKey(conversationId: string) {
  return { parentId: conversationId, ...MESSAGES_CACHE_PARAMS } as const;
}

// Status monotonicity
// A status update is only applied if it moves forward in this sequence.
// 'failed' is a terminal branch handled separately by callers.
export const STATUS_ORDER: Record<string, number> = {
  pending: 0,
  sending: 1,
  unknown: 2,
  sent: 3,
  delivered: 4,
  read: 5,
};

/** Returns true if `next` is strictly forward from `current` in the delivery lifecycle. */
export function isForwardStatus(current: string, next: string): boolean {
  const cur = STATUS_ORDER[current.toLowerCase()];
  const nxt = STATUS_ORDER[next.toLowerCase()];
  // Statuses not in the ordering (e.g., 'failed') bypass — handled by the caller
  if (cur === undefined || nxt === undefined) return true;
  return nxt > cur;
}

// Temp message identity
export function isTempMessage(id: string | undefined | null): boolean {
  return typeof id === 'string' && id.startsWith('temp_');
}

export function isFailedTempMessage(
  msg: { id?: string; status?: string } | null | undefined
): boolean {
  return !!msg && isTempMessage(msg.id) && msg.status === 'failed';
}

// Timing constants

/** Temp messages younger than this are preserved during refetches (in-flight). */
export const TEMP_MESSAGE_TTL_MS = 10_000;

/** Heuristic matching window for temp→real message reconciliation. */
export const RECONCILIATION_WINDOW_MS = 15_000;

/** Safety net: refetch if no send response within this time.
 *  Inline MMS can take up to ~14s (8s file processing + 6s Twilio).
 *  The timeout is cleared when the HTTP response arrives, so text SMS
 *  resolves immediately — the 20s only matters for slow MMS sends. */
export const SEND_SAFETY_TIMEOUT_MS = 20_000;

// Compose sentinel
export const COMPOSE_SENTINEL = '__COMPOSE__' as const;

export function isComposeSentinel(id: string | null | undefined): boolean {
  return id === COMPOSE_SENTINEL;
}
